import { Instant } from '@js-joda/core';
import { batch, computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { DefaultError } from '@tanstack/query-core';
import type { Access, SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import { EmptyArray } from '@thinkalpha/common/util/empty.js';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { optional } from 'inversify';
import { createStructuredSelector } from 'reselect';
import { inject, injectable } from 'src/features/ioc';
import { allWidgets } from 'src/lib/widget/widgets';
import type { AllowedWidgetsModel, WidgetEntry } from 'src/models/AllowedWidgetsModel';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import type { SessionService } from 'src/services/SessionService';
import { sessionUserDetails } from 'src/services/SessionService/userDetails';
import type { RootState } from 'src/store';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

const selectAllowedWidgetsInputs = createStructuredSelector({
    user: (state: RootState) => state.auth?.user,
    masqUser: (state: RootState) => state.auth?.masqUser,
    superuserAccessEnabledUntil: (state: RootState) => state.ui.superuserAccessEnabledUntil,
});

type AllowedWidgetsInputs = ReturnType<typeof selectAllowedWidgetsInputs>;
type SecurityPackageQuery = QueryObserverModel<
    z.infer<typeof SecurityPackage>,
    DefaultError,
    z.infer<typeof SecurityPackage>
>;

/** Stable base list; per-user adjustments are layered on in computeds. */
const baseEntries: WidgetEntry[] = Object.values(allWidgets).map((widgetDefinition) => ({
    widgetDefinition,
    allowedByIsDev: false,
}));

const hasRoleEntitlement = (entry: WidgetEntry, userRoles: readonly string[]): boolean =>
    entry.widgetDefinition.roles.some((role) => userRoles.includes(role));

@injectable()
export class AllowedWidgetsModelImpl implements AllowedWidgetsModel {
    readonly #disposableStack = new DisposableStack();
    readonly #securityPackageQuery: SecurityPackageQuery;
    readonly #userId = signal<string | undefined>(undefined);
    readonly #isSuperuserEnabled = signal(false);
    readonly #isDeveloper = signal(false);
    readonly #entries: ReadonlySignal<WidgetEntry[]>;
    readonly #allowedWidgetTypes: ReadonlySignal<ReadonlySet<WidgetType>>;
    readonly #isReady: ReadonlySignal<boolean>;
    readonly #hasResolvedOnce = signal(false);
    #storeInputs: AllowedWidgetsInputs;
    #sessionUser: UserDetails | undefined;

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('SessionService') @optional() sessionService?: SessionService,
    ) {
        this.#storeInputs = selectAllowedWidgetsInputs(store.getState());
        this.#sessionUser = sessionService ? sessionUserDetails(sessionService.state.value) : undefined;
        this.#updateSignals();
        this.#disposableStack.defer(
            store.subscribeSelector(selectAllowedWidgetsInputs).handle((inputs) => {
                this.#storeInputs = inputs;
                this.#updateSignals();
            }),
        );
        if (sessionService) {
            this.#disposableStack.defer(
                effect(() => {
                    this.#sessionUser = sessionUserDetails(sessionService.state.value);
                    this.#updateSignals();
                }),
            );
        }

        this.#securityPackageQuery = queryObserverModelFactory(
            computed(() => getUserSecurityPackageQuery(this.#userId.value)),
        );
        this.#disposableStack.use(this.#securityPackageQuery);

        const userRoles: ReadonlySignal<readonly string[]> = computed(() => {
            const data = this.#securityPackageQuery.signal.value.data;
            return data === undefined ? EmptyArray : data.roles.map((role) => role.role);
        });
        const accessList: ReadonlySignal<readonly z.infer<typeof Access>[]> = computed(
            () => this.#securityPackageQuery.signal.value.data?.accessList ?? EmptyArray,
        );

        this.#entries = computed(() => {
            if (this.#isSuperuserEnabled.value) {
                return baseEntries;
            }

            const roles = userRoles.value;
            if (this.#isDeveloper.value) {
                return baseEntries.map((entry) => ({
                    ...entry,
                    allowedByIsDev: !hasRoleEntitlement(entry, roles),
                }));
            }

            const filtered = baseEntries.filter((entry) => hasRoleEntitlement(entry, roles));

            const seesRiskAsSeparateCategory = accessList.value.some((entry) => entry.type === 'firm');
            if (seesRiskAsSeparateCategory) {
                return filtered;
            }

            return filtered.map((entry) =>
                entry.widgetDefinition.category === 'widgets::categories::risk'
                    ? { ...entry, categoryOverride: 'widgets::categories::trading' as const }
                    : entry,
            );
        });

        this.#allowedWidgetTypes = computed(
            () => new Set(this.#entries.value.map((entry) => entry.widgetDefinition.type)),
        );

        this.#isReady = computed(() => {
            if (this.#isSuperuserEnabled.value || this.#isDeveloper.value) {
                return true;
            }
            const result = this.#securityPackageQuery.signal.value;
            return result.data !== undefined || result.status === 'error';
        });
        this.#disposableStack.defer(
            effect(() => {
                if (this.#isReady.value) {
                    this.#hasResolvedOnce.value = true;
                }
            }),
        );
    }

    #updateSignals(): void {
        const effectiveUser = this.#storeInputs.masqUser ?? this.#sessionUser ?? this.#storeInputs.user;
        batch(() => {
            this.#userId.value = effectiveUser?.id;
            this.#isDeveloper.value = effectiveUser?.isDeveloper ?? false;
            this.#isSuperuserEnabled.value = Boolean(
                effectiveUser?.isSuperuser && this.#storeInputs.superuserAccessEnabledUntil?.isAfter(Instant.now()),
            );
        });
    }

    get entries(): ReadonlySignal<WidgetEntry[]> {
        return this.#entries;
    }

    get allowedWidgetTypes(): ReadonlySignal<ReadonlySet<WidgetType>> {
        return this.#allowedWidgetTypes;
    }

    get isReady(): ReadonlySignal<boolean> {
        return this.#isReady;
    }

    get hasResolvedOnce(): ReadonlySignal<boolean> {
        return this.#hasResolvedOnce;
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
